import type {
  EmployeeExecutionBlocker,
  TCEmployeeExecution,
} from 'src/api/generated';
import {
  ExecutionOutcome,
  type ExecutionBlockerPresentation,
  type ExecutionOutcomeValue,
} from 'src/modules/automations/types/executions/execution-presentation.type';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isStructuredExecutionBlocker = (
  value: unknown
): value is EmployeeExecutionBlocker => {
  if (
    !isRecord(value) ||
    typeof value.reason !== 'string' ||
    typeof value.occurredAt !== 'string'
  ) {
    return false;
  }

  if (value.category === 'missing-capability') {
    return typeof value.capability === 'string';
  }

  if (value.category === 'repeated-tool-failure') {
    return (
      typeof value.toolName === 'string' && typeof value.attempts === 'number'
    );
  }

  return false;
};

/** Reads the optional structured blocker from compatible execution-state versions. */
export const getExecutionBlocker = (
  execution: TCEmployeeExecution
): ExecutionBlockerPresentation | null => {
  const structured: unknown = execution.state?.blocker;
  if (isStructuredExecutionBlocker(structured)) {
    return {
      structured,
      reason: structured.reason,
    };
  }

  const state: unknown = execution.state;
  if (!isRecord(state) || !isRecord(state.blocker)) {
    return null;
  }

  const reason = state.blocker.reason;
  return {
    ...(typeof reason === 'string' ? { reason } : {}),
  };
};

/** Resolves the business-facing execution outcome from persisted state. */
export const getExecutionOutcome = (
  execution: TCEmployeeExecution
): ExecutionOutcomeValue => {
  const executionState: unknown = execution.state;
  const state = isRecord(executionState) ? executionState : undefined;
  if (state?.objectiveOutcome === 'blocked' || getExecutionBlocker(execution)) {
    return ExecutionOutcome.BLOCKED;
  }

  const status: unknown = execution.status;
  if (status === 'completed') {
    return ExecutionOutcome.COMPLETED;
  }
  if (status === 'failed') {
    return ExecutionOutcome.FAILED;
  }
  if (status === 'running') {
    return ExecutionOutcome.RUNNING;
  }
  return ExecutionOutcome.UNKNOWN;
};

/** Returns the primary conversation contact name, when available. */
export const getExecutionContactName = (
  execution: TCEmployeeExecution
): string | null => {
  const contacts = execution.inputs.initialContext.conversationContacts ?? [];
  if (contacts.length === 0) return null;
  const contactName = formatFullName(
    contacts[0].firstName,
    contacts[0].lastName
  );
  return contactName.length > 0 ? contactName : null;
};
